import React, { useState, useEffect, useMemo } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tab,
  Tabs,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Edit,
  Search,
  Visibility,
  Close,
  Phone,
  Email,
  LocationOn,
  ContactPhone,
  CheckCircle,
  Cancel,
  Pending,
  Person,
  History,
} from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import profileService from "../../services/profileService";

// Validation helpers
const isValidPhone = (value) => {
  const digits = (value || "").replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};
const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

// Emergency contact name: letters only (no digits), 3–50 chars when provided
const emergencyContactNameNoNumbers = (value) => (value || "").replace(/[0-9]/g, "");
const isValidEmergencyContactName = (value) => {
  const v = (value || "").trim();
  if (!v) return true; // optional field
  return v.length >= 3 && v.length <= 50 && !/[0-9]/.test(v);
};

const ContactInfoRequests = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    phone: "",
    email: "",
    address: "",
    emergencyContact: "",
    emergencyPhone: "",
    reason: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [requestsList, setRequestsList] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [requestsError, setRequestsError] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewRequestId, setReviewRequestId] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // "approve" | "reject"
  const [reviewComment, setReviewComment] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Compact contact info from GET /profile/contact
  const [profileContact, setProfileContact] = useState({
    phone: "",
    email: "",
    address: "",
    emergencyContact: "",
    emergencyPhone: "",
  });
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState(null);

  // Map API response to summary rows (one per request) with fields array for expandable detail
  const mapApiResponseToSummaries = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      const requestId = item.id;
      const contactUser = item.contactUser ?? item.contact_user ?? {};
      const requestedData = item.requestedData ?? item.requested_data ?? {};
      const user = contactUser.fullName ?? contactUser.full_name ?? "—";
      const userRole = (item.entityType ?? "").toLowerCase() || "—";
      const status = (item.status ?? "").toLowerCase() || "pending";
      const requestedAt = item.createdAt
        ? (typeof item.createdAt === "string"
            ? new Date(item.createdAt).toLocaleString()
            : new Date(item.createdAt).toLocaleString?.())
        : "—";
      const reason = requestedData.reasonForChange ?? item.reviewComment ?? "";

      const phoneNew = requestedData.phoneNumber ?? requestedData.phone_number ?? "";
      const emailNew = requestedData.emailAddress ?? requestedData.email_address ?? "";
      const addressNew = requestedData.address ?? "";
      const emergencyName = requestedData.emergencyContactName ?? requestedData.emergency_contact_name ?? "";
      const emergencyPhone = requestedData.emergencyContactPhone ?? requestedData.emergency_contact_phone ?? "";
      const contactPhone = contactUser.phoneNumber ?? contactUser.phone_number ?? "";
      const contactEmail = contactUser.email ?? "";

      const fieldList = [
        { requestType: "phone", oldValue: contactPhone, newValue: phoneNew },
        { requestType: "email", oldValue: contactEmail, newValue: emailNew },
        { requestType: "address", oldValue: "", newValue: addressNew },
        {
          requestType: "emergencyContact",
          oldValue: "",
          newValue: [emergencyName, emergencyPhone].filter(Boolean).join(", ") || emergencyName || emergencyPhone,
        },
      ].filter((f) => f.newValue);

      return {
        id: requestId,
        requestId,
        user,
        userRole,
        status,
        requestedAt,
        reason,
        reviewedBy: item.reviewedBy,
        reviewComment: item.reviewComment,
        userId: item.userId,
        contactUser,
        contactReviewer: item.contactReviewer,
        fields: fieldList,
      };
    });
  };

  const fetchProfileContact = async () => {
    setContactLoading(true);
    setContactError(null);
    try {
      const res = await profileService.getProfileContact();
      const data = res?.data?.data ?? res?.data ?? {};
      setProfileContact({
        phone: data.phone ?? data.phoneNumber ?? "",
        email: data.email ?? data.emailAddress ?? "",
        address: data.address ?? "",
        emergencyContact: data.emergencyContact ?? data.emergency_contact ?? data.emergencyContactName ?? "",
        emergencyPhone: data.emergency ?? data.emergencyPhone ?? data.emergency_phone ?? data.emergencyContactPhone ?? "",
      });
    } catch (err) {
      const message =
        err.response?.data?.message ??
        err.response?.data?.error ??
        err.message ??
        "Failed to load contact information.";
      setContactError(message);
    } finally {
      setContactLoading(false);
    }
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    setRequestsError(null);
    try {
      const res = await profileService.getInstituteContactUpdateRequests();
      const body = res?.data;
      // Support multiple response shapes: { data: [] }, { list: [] }, or array at top level
      let list = [];
      if (Array.isArray(body)) {
        list = body;
      } else if (body && Array.isArray(body.data)) {
        list = body.data;
      } else if (body && Array.isArray(body.list)) {
        list = body.list;
      } else if (body && Array.isArray(body.requests)) {
        list = body.requests;
      }
      const summaries = mapApiResponseToSummaries(list);
      setRequestsList(summaries);
    } catch (err) {
      const message =
        err.response?.data?.message ??
        err.response?.data?.error ??
        err.message ??
        "Failed to load requests.";
      setRequestsError(message);
      setRequestsList([]);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    fetchProfileContact();
  }, []);

  useEffect(() => {
    if (submitSuccess) {
      fetchRequests();
      fetchProfileContact();
      setSubmitSuccess(false);
    }
  }, [submitSuccess]);

  const requests = requestsList;
  const pendingRequests = useMemo(
    () => requestsList.filter((r) => (r.status || "").toLowerCase() === "pending"),
    [requestsList]
  );
  const myRequests = useMemo(() => {
    const uid = user?.id ?? user?.userId;
    if (!uid) return requestsList;
    return requestsList.filter((r) => Number(r.userId) === Number(uid));
  }, [requestsList, user?.id, user?.userId]);

  // Hide "My Requests" tab for institute role (frontend normalizes institute → admin)
  const showMyRequestsTab = useMemo(() => {
    const r = (user?.role ?? "").toLowerCase();
    return r !== "institute" && r !== "admin";
  }, [user?.role]);

  // When My Requests tab is hidden, clamp activeTab so it stays valid
  const effectiveTab = showMyRequestsTab ? activeTab : Math.min(activeTab, 1);

  const tabFilteredRequests = useMemo(() => {
    const tab = showMyRequestsTab ? activeTab : Math.min(activeTab, 1);
    if (tab === 1) return pendingRequests;
    if (tab === 2) return myRequests;
    return requestsList;
  }, [activeTab, showMyRequestsTab, pendingRequests, myRequests, requestsList]);

  const filteredSummaries = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return tabFilteredRequests;
    return tabFilteredRequests.filter((r) => {
      const user = (r.user ?? "").toLowerCase();
      const role = (r.userRole ?? "").toLowerCase();
      const status = (r.status ?? "").toLowerCase();
      const requestedAt = (r.requestedAt ?? "").toLowerCase();
      return [user, role, status, requestedAt].some((s) => s.includes(q));
    });
  }, [searchQuery, tabFilteredRequests]);

  const displayRows = useMemo(() => {
    return filteredSummaries.map((summary) => {
      const requestId = summary.requestId ?? summary.id;
      return {
        ...summary,
        id: `summary-${requestId}`,
        fieldCount: (summary.fields || []).length,
      };
    });
  }, [filteredSummaries]);

  const getFieldIcon = (type) => {
    switch (type) {
      case "phone":
        return <Phone />;
      case "email":
        return <Email />;
      case "address":
        return <LocationOn />;
      case "emergencyContact":
        return <ContactPhone />;
      default:
        return <Person />;
    }
  };

  const getFieldLabel = (type) => {
    switch (type) {
      case "phone":
        return "Phone Number";
      case "email":
        return "Email Address";
      case "address":
        return "Address";
      case "emergencyContact":
        return "Emergency Contact";
      default:
        return type;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle fontSize="small" />;
      case "rejected":
        return <Cancel fontSize="small" />;
      case "pending":
        return <Pending fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const statusLabel = (status) => {
    const s = (status || "").toLowerCase();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
  };

  const hideActionsForRole = useMemo(() => {
    const r = (user?.role ?? "").toLowerCase();
    return r === "teacher" || r === "hod";
  }, [user?.role]);

  // Show "Request Edit" only for teacher and HOD; hide for institute/admin
  const showRequestEditButton = useMemo(() => {
    const r = (user?.role ?? "").toLowerCase();
    return r === "teacher" || r === "hod";
  }, [user?.role]);

  const isTeacherOrHod = showRequestEditButton;
  const profileEditRequestsPath = `/${String(user?.role || "hod").toLowerCase()}/requests?status=pending`;

  const baseColumns = [
    {
      field: "user",
      headerName: "User",
      width: 220,
      renderCell: (params) => {
        const val = params.value ?? "—";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32 }}>{(val || "—")[0]}</Avatar>
            <Box>
              <Typography variant="body2">{val}</Typography>
              <Typography variant="caption" color="text.secondary">
                {params.row.userRole}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "requestType",
      headerName: "Field",
      width: 200,
      renderCell: (params) => {
        const n = params.row.fieldCount ?? 0;
        return (
          <Typography variant="body2" color="text.secondary">
            {n} field{n !== 1 ? "s" : ""} requested
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={statusLabel(params.value)}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: "requestedAt",
      headerName: "Requested At",
      width: 220,
    },
  ];

  const actionsColumn = {
    field: "actions",
    headerName: "Actions",
    width: 130,
    renderCell: (params) => {
      const requestId = params.row.requestId ?? params.row.id?.replace?.("summary-", "");
      const isPending = (params.row.status || "").toLowerCase() === "pending";
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedRequest(params.row);
              setViewDialogOpen(true);
            }}
            aria-label="View details"
          >
            <Visibility />
          </IconButton>
          {isPending && requestId && (
            <>
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  openReviewDialog(requestId, "approved");
                }}
                disabled={reviewLoading}
                aria-label="Approve"
              >
                <CheckCircle />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  openReviewDialog(requestId, "rejected");
                }}
                disabled={reviewLoading}
                aria-label="Reject"
              >
                <Cancel />
              </IconButton>
            </>
          )}
        </Box>
      );
    },
  };

  const columns = hideActionsForRole ? baseColumns : [...baseColumns, actionsColumn];

  const validateForm = () => {
    const errors = {};
    const trimmedReason = (formData.reason || "").trim();
    const trimmedPhone = (formData.phone || "").trim();
    const trimmedEmail = (formData.email || "").trim();
    const trimmedEmergencyPhone = (formData.emergencyPhone || "").trim();

    // Required: Phone Number (same message as Profile.js)
    if (!trimmedPhone) {
      errors.phone = "Phone number is required.";
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = "Invalid phone number";
    }

    // Required: Email
    if (!trimmedEmail) {
      errors.email = "Email address is required.";
    } else if (!isValidEmail(formData.email)) {
      errors.email = "Enter a valid email address.";
    }

    // Required: Emergency Contact Phone (same message as Profile.js)
    if (!trimmedEmergencyPhone) {
      errors.emergencyPhone = "Emergency contact phone is required.";
    } else if (!isValidPhone(formData.emergencyPhone)) {
      errors.emergencyPhone = "Invalid phone number";
    }

    // Required: Reason for change (min 3, max 100 chars)
    if (!trimmedReason) {
      errors.reason = "Reason for change is required.";
    } else if (trimmedReason.length < 3) {
      errors.reason = "Reason must be at least 3 characters.";
    } else if (trimmedReason.length > 100) {
      errors.reason = "Reason must be at most 100 characters.";
    }

    // Optional: Emergency Contact Name — if filled: no numbers, 3–50 chars
    const trimmedEmergencyName = (formData.emergencyContact || "").trim();
    if (trimmedEmergencyName && !isValidEmergencyContactName(formData.emergencyContact)) {
      if (/[0-9]/.test(formData.emergencyContact)) {
        errors.emergencyContact = "Numbers are not allowed in name.";
      } else if (trimmedEmergencyName.length < 3) {
        errors.emergencyContact = "Name must be at least 3 characters.";
      } else {
        errors.emergencyContact = "Name must be at most 50 characters.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCloseRequestDialog = () => {
    setRequestDialogOpen(false);
    setFormErrors({});
    setSubmitError(null);
    setFormData({
      phone: "",
      email: "",
      address: "",
      emergencyContact: "",
      emergencyPhone: "",
      reason: "",
    });
  };

  const handleSubmitRequest = async () => {
    setSubmitError(null);
    if (!validateForm()) return;

    const entityType = (user?.role || "").toLowerCase();
    const instituteId = user?.instituteId ?? user?.institute_id ?? 0;

    if (!user) {
      setSubmitError("You must be logged in to submit a request.");
      return;
    }
    if (!entityType) {
      setSubmitError("User role could not be determined.");
      return;
    }

    const requestedData = {};
    const trim = (v) => (v == null ? "" : String(v)).trim();
    const digitsOnly = (v) => (v == null ? "" : String(v).replace(/\D/g, ""));
    if (trim(formData.phone))
      requestedData.phoneNumber = digitsOnly(formData.phone);
    if (trim(formData.email)) requestedData.emailAddress = trim(formData.email);
    if (trim(formData.address)) requestedData.address = trim(formData.address);
    if (trim(formData.emergencyContact))
      requestedData.emergencyContactName = trim(formData.emergencyContact);
    if (trim(formData.emergencyPhone))
      requestedData.emergencyContactPhone = digitsOnly(formData.emergencyPhone);
    requestedData.reasonForChange = trim(formData.reason);

    setSubmitLoading(true);
    try {
      await profileService.createInstituteContactUpdateRequest({
        entityType,
        instituteId: Number(instituteId),
        requestedData,
      });
      setSubmitSuccess(true);
      handleCloseRequestDialog();
      // Optionally refetch requests list here if you have an API for it
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to submit contact update request.";
      setSubmitError(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const openReviewDialog = (requestId, action) => {
    setReviewRequestId(requestId);
    setReviewAction(action);
    setReviewComment("");
    setReviewError(null);
    setReviewDialogOpen(true);
  };

  const closeReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewRequestId(null);
    setReviewAction(null);
    setReviewComment("");
    setReviewError(null);
  };
  const closeRejectDialog = closeReviewDialog; // alias for backward compatibility

  const handleReviewSubmit = async () => {
    if (!reviewRequestId || !reviewAction) return;
    const comment = reviewComment.trim();
    if (reviewAction === "rejected" && !comment) {
      setReviewError("Please enter a reason for rejecting this request.");
      return;
    }
    setReviewError(null);
    setReviewLoading(true);
    try {
      await profileService.reviewInstituteContactUpdateRequest(reviewRequestId, {
        action: reviewAction === "approved" ? "approve" : "reject",
        comment: reviewAction === "rejected" ? comment : "",
      });
      setReviewSuccess(true);
      closeReviewDialog();
      setViewDialogOpen(false);
      await fetchRequests();
    } catch (err) {
      const message =
        err.response?.data?.message ??
        err.response?.data?.error ??
        err.message ??
        `Failed to ${reviewAction} request.`;
      setReviewError(message);
    } finally {
      setReviewLoading(false);
    }
  };

  useEffect(() => {
    if (!submitSuccess) return;
    const t = setTimeout(() => setSubmitSuccess(false), 5000);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  return (
    <Box>
      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess(false)}>
          Contact update request submitted successfully.
        </Alert>
      )}
      {reviewSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReviewSuccess(false)}>
          Request reviewed successfully.
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Contact Information Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request and manage contact information updates
          </Typography>
        </Box>
        {showRequestEditButton && (
          <Button
            variant="contained"
            color="warning"
            onClick={() => navigate(profileEditRequestsPath)}
            sx={{ mr: 1 }}
          >
            View Profile Edit Requests
          </Button>
        )}
        {showRequestEditButton && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => setRequestDialogOpen(true)}
          >
            Request Contact Edit
          </Button>
        )}
      </Box>

      <Alert
        severity="info"
        sx={{ mb: 3 }}
        action={
          isTeacherOrHod ? (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => navigate(profileEditRequestsPath)}
            >
              Profile Edit Requests
            </Button>
          ) : null
        }
      >
        This page is for contact detail changes (phone, email, address, emergency contact).
        {isTeacherOrHod
          ? " To track a pending profile edit permission request, open Profile Edit Requests."
          : " Requests to enable profile editing are managed separately under Profile Edit Requests."}
      </Alert>

      {/* Current Contact Info Card - from GET /profile/contact */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Your Current Contact Information
          </Typography>
          {contactError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setContactError(null)}>
              {contactError}
            </Alert>
          )}
          {contactLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 48 }}>
                  <Avatar sx={{ bgcolor: "primary.main", flexShrink: 0 }}>
                    <Phone />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {profileContact.phone || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 48 }}>
                  <Avatar sx={{ bgcolor: "secondary.main", flexShrink: 0 }}>
                    <Email />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {profileContact.email || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 48 }}>
                  <Avatar sx={{ bgcolor: "success.main", flexShrink: 0 }}>
                    <LocationOn />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {profileContact.address || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minHeight: 48 }}>
                  <Avatar sx={{ bgcolor: "warning.main", flexShrink: 0 }}>
                    <ContactPhone />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Emergency
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {profileContact.emergencyPhone || profileContact.emergencyContact || "—"}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          {
            label: "Pending Requests",
            value: pendingRequests.length,
            color: "warning.main",
          },
          {
            label: "Approved",
            value: requests.filter((r) => r.status === "approved").length,
            color: "success.main",
          },
          {
            label: "Rejected",
            value: requests.filter((r) => r.status === "rejected").length,
            color: "error.main",
          },
          {
            label: "Total Requests",
            value: requests.length,
            color: "info.main",
          },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={effectiveTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="All Requests" icon={<History />} iconPosition="start" />
          <Tab
            label={`Pending (${pendingRequests.length})`}
            icon={<Pending />}
            iconPosition="start"
          />
          {showMyRequestsTab && (
            <Tab label="My Requests" icon={<Person />} iconPosition="start" />
          )}
        </Tabs>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardContent>
          {requestsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRequestsError(null)}>
              {requestsError}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder="Search requests..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ minHeight: 320 }}>
            {loadingRequests ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 320,
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading requests…
                </Typography>
              </Box>
            ) : displayRows.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 320,
                  gap: 1,
                  color: "text.secondary",
                }}
              >
                <History sx={{ fontSize: 48, opacity: 0.5 }} />
                <Typography variant="body1">
                  {searchQuery.trim()
                    ? "No requests match your search."
                    : "No contact update requests yet."}
                </Typography>
                <Typography variant="body2">
                  {searchQuery.trim()
                    ? "Try a different search term."
                    : "Use \"Request Edit\" to submit a new request."}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                rows={displayRows}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                initialState={{ pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } } }}
                sx={{ border: "none" }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Request Edit Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseRequestDialog();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Request Contact Information Edit
            <IconButton onClick={handleCloseRequestDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            <strong>Required:</strong> Phone number, email, emergency contact phone, and reason for change. Address and emergency contact name are optional.
          </Alert>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.phone} required>
                <InputLabel sx={{ top: "-5px" }} shrink htmlFor="contact-phone" required>
                  Phone Number
                </InputLabel>
                <PhoneInput
                  country="in"
                  specialLabel=""
                  inputProps={{
                    id: "contact-phone",
                    name: "phone",
                    "aria-label": "Phone Number",
                  }}
                  countryCodeEditable={false}
                  enableSearch
                  value={formData.phone || ""}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, phone: value }));
                    // Real-time validation (same as Profile.js): show "Invalid phone number" as user types
                    if (!value || String(value).trim() === "") {
                      setFormErrors((prev) => ({ ...prev, phone: "" }));
                    } else {
                      const digitsOnly = (value || "").replace(/\D/g, "");
                      setFormErrors((prev) => ({
                        ...prev,
                        phone: digitsOnly.length < 10 ? "Invalid phone number" : "",
                      }));
                    }
                  }}
                  containerStyle={{ marginTop: "8px" }}
                  inputStyle={{
                    width: "100%",
                    height: "56px",
                    paddingLeft: "48px",
                    borderRadius: "4px",
                    border: formErrors.phone
                      ? "1px solid #d32f2f"
                      : "1px solid rgba(0, 0, 0, 0.23)",
                  }}
                />
                {formErrors.phone && (
                  <FormHelperText>{formErrors.phone}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  if (formErrors.email) setFormErrors((prev) => ({ ...prev, email: "" }));
                }}
                placeholder={profileContact.email}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                InputLabelProps={{ required: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder={profileContact.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={formData.emergencyContact}
                onChange={(e) => {
                  const v = e.target.value;
                  const lettersOnly = emergencyContactNameNoNumbers(v);
                  const capped = lettersOnly.length > 50 ? lettersOnly.slice(0, 50) : lettersOnly;
                  setFormData((prev) => ({ ...prev, emergencyContact: capped }));
                  if (formErrors.emergencyContact)
                    setFormErrors((prev) => ({ ...prev, emergencyContact: "" }));
                }}
                onKeyDown={(e) => {
                  if (/[0-9]/.test(e.key)) e.preventDefault();
                }}
                placeholder={profileContact.emergencyContact}
                error={!!formErrors.emergencyContact}
                helperText={formErrors.emergencyContact}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth error={!!formErrors.emergencyPhone} required>
                <InputLabel sx={{ top: "-5px" }} shrink htmlFor="emergency-phone" required>
                  Emergency Contact Phone
                </InputLabel>
                <PhoneInput
                  country="in"
                  specialLabel=""
                  inputProps={{
                    id: "emergency-phone",
                    name: "emergencyPhone",
                    "aria-label": "Emergency Contact Phone",
                  }}
                  countryCodeEditable={false}
                  enableSearch
                  value={formData.emergencyPhone || ""}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, emergencyPhone: value }));
                    // Real-time validation (same as Profile.js): show "Invalid phone number" as user types
                    if (!value || String(value).trim() === "") {
                      setFormErrors((prev) => ({ ...prev, emergencyPhone: "" }));
                    } else {
                      const digitsOnly = (value || "").replace(/\D/g, "");
                      setFormErrors((prev) => ({
                        ...prev,
                        emergencyPhone: digitsOnly.length < 10 ? "Invalid phone number" : "",
                      }));
                    }
                  }}
                  containerStyle={{ marginTop: "8px" }}
                  inputStyle={{
                    width: "100%",
                    height: "56px",
                    paddingLeft: "48px",
                    borderRadius: "4px",
                    border: formErrors.emergencyPhone
                      ? "1px solid #d32f2f"
                      : "1px solid rgba(0, 0, 0, 0.23)",
                  }}
                />
                {formErrors.emergencyPhone && (
                  <FormHelperText>{formErrors.emergencyPhone}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Change"
                multiline
                rows={2}
                value={formData.reason}
                onChange={(e) => {
                  setFormData({ ...formData, reason: e.target.value });
                  if (formErrors.reason) setFormErrors((prev) => ({ ...prev, reason: "" }));
                }}
                placeholder="Please provide a reason for the contact information change..."
                error={!!formErrors.reason}
                helperText={formErrors.reason}
                required
                InputLabelProps={{ required: true }}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestDialog} disabled={submitLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={submitLoading}
            startIcon={submitLoading ? <CircularProgress size={20} /> : null}
          >
            {submitLoading ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Contact Information Request Details
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <Avatar sx={{ width: 50, height: 50 }}>
                  {selectedRequest.user[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedRequest.user}</Typography>
                  <Chip
                    icon={getStatusIcon(selectedRequest.status)}
                    label={selectedRequest.status}
                    size="small"
                    color={getStatusColor(selectedRequest.status)}
                  />
                </Box>
              </Box>

              {selectedRequest.fields?.length ? (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Requested changes
                  </Typography>
                  {selectedRequest.fields.map((f, idx) => (
                    <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          {getFieldIcon(f.requestType)}
                          <Typography variant="body2" fontWeight={500}>
                            {getFieldLabel(f.requestType)}
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Old Value
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: "line-through", color: "text.secondary" }}
                            >
                              {f.oldValue || "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              New Value
                            </Typography>
                            <Typography variant="body2" color="primary" fontWeight={500}>
                              {f.newValue || "—"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                <>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        {getFieldIcon(selectedRequest.requestType)}
                      </ListItemIcon>
                      <ListItemText
                        primary={getFieldLabel(selectedRequest.requestType)}
                        secondary="Field to update"
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Old Value
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            {selectedRequest.oldValue}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            New Value
                          </Typography>
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight={500}
                          >
                            {selectedRequest.newValue}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                Reason
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedRequest.reason}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Requested At
              </Typography>
              <Typography variant="body2">
                {selectedRequest.requestedAt}
              </Typography>

              {selectedRequest.status === "rejected" && (selectedRequest.reviewComment || selectedRequest.rejectionReason) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Rejection Reason:</strong>{" "}
                  {selectedRequest.reviewComment ?? selectedRequest.rejectionReason}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedRequest?.status === "pending" && (selectedRequest?.requestId ?? selectedRequest?.id) && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => openReviewDialog(selectedRequest.requestId ?? selectedRequest.id, "rejected")}
                disabled={reviewLoading}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => openReviewDialog(selectedRequest.requestId ?? selectedRequest.id, "approved")}
                disabled={reviewLoading}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Review dialog: reject reason only (no reason box for approve) */}
      <Dialog open={reviewDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; closeReviewDialog(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === "approved" ? "Approve Request" : "Reject Request"}
        </DialogTitle>
        <DialogContent>
          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewError(null)}>
              {reviewError}
            </Alert>
          )}
          {reviewAction === "rejected" && (
            <TextField
              fullWidth
              label="Rejection Reason *"
              multiline
              rows={3}
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Provide a reason for rejecting this request..."
              required
              InputLabelProps={{ required: true }}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDialog} disabled={reviewLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={reviewAction === "rejected" ? "error" : "success"}
            onClick={handleReviewSubmit}
            disabled={reviewLoading}
            startIcon={reviewLoading ? <CircularProgress size={20} /> : (reviewAction === "approved" ? <CheckCircle /> : <Cancel />)}
          >
            {reviewLoading
              ? (reviewAction === "approved" ? "Approving…" : "Rejecting…")
              : (reviewAction === "approved" ? "Approve" : "Reject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactInfoRequests;
